import discountQueries from '@apis/queries/discountQueries';
import Button from '@components/Button';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import { POINT_USE_UNIT } from '@constants/orders';
import styled from '@emotion/styled';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import SectionTitle from '../common/SectionTitle';

/**
 * 포인트 사용. 1,000P 단위로 보유 포인트와 남은 결제 금액 안에서 쓸 수 있다.
 * 입력을 마치거나(blur) 전액 사용을 누르면 포인트 할인으로 반영한다.
 */
function PointUse() {
    const { t } = useTranslation('order');
    const dispatch = useDispatch();
    const {
        shopId, shopName, totalPointDiscount, paymentAmount,
    } = useCalculatedRequest();
    const { data } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });
    const points = data?.points || 0;
    // 포인트를 뺀 결제 금액과 보유 포인트 중 작은 값을 단위로 내림
    const maxUsable = Math.floor(
        Math.min(points, paymentAmount + totalPointDiscount) / POINT_USE_UNIT,
    ) * POINT_USE_UNIT;
    const [value, setValue] = useState<string>(totalPointDiscount ? `${totalPointDiscount}` : '');
    const [error, setError] = useState<string>('');

    // 다른 할인 적용으로 포인트가 해제되면 입력값도 비운다
    useEffect(() => {
        setValue(totalPointDiscount ? `${totalPointDiscount}` : '');
    }, [totalPointDiscount]);

    const apply = (amount: number) => {
        if (amount % POINT_USE_UNIT !== 0) {
            setError(t('point.unitError', { unit: POINT_USE_UNIT.toLocaleString() }));
            return;
        }
        if (amount > maxUsable) {
            setError(t('point.maxError', { max: maxUsable.toLocaleString() }));
            return;
        }
        setError('');
        dispatch(orderActions.setPointDiscount(amount));
    };

    if (!data?.points) {
        return null;
    }
    return (
        <Container fullWidth spacing={4}>
            <Row fullWidth justify='space-between'>
                <SectionTitle>{t('point.title')}</SectionTitle>
                <Text size='body-long' color={colors.textThird}>
                    {t('point.owned')}{' '}
                    <PointText as='span' size='body-long' fontWeight='bold'>
                        {points.toLocaleString()}P
                    </PointText>
                    {' · '}{shopName}
                </Text>
            </Row>
            <Row fullWidth spacing={2} align='flex-start'>
                <FieldWrapper>
                    <TextField value={value}
                        inputMode='numeric'
                        placeholder={t('point.placeholder', { unit: POINT_USE_UNIT.toLocaleString() })}
                        onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={() => apply(Number(value || 0))}
                        errorText={error}
                        helperText={error ? undefined : t('point.helper', {
                            unit: POINT_USE_UNIT.toLocaleString(),
                            max: maxUsable.toLocaleString(),
                        })} />
                </FieldWrapper>
                <UseAllButton variant='outlined'
                    size='small'
                    type='button'
                    disabled={maxUsable === 0}
                    onClick={() => {
                        setValue(`${maxUsable}`);
                        apply(maxUsable);
                    }}>
                    {t('point.useAll')}
                </UseAllButton>
            </Row>
        </Container>
    );
}

const Container = styled(Column)`
    padding: var(--spacing-4);
`;

const PointText = styled(Text)`
    color: var(--color-primary);
`;

const FieldWrapper = styled.div`
    flex: 1;
    min-width: 0;
`;

const UseAllButton = styled(Button)`
    flex-shrink: 0;
    height: 40px;
    min-width: 100px;
`;

export default PointUse;
